import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AppHeader } from '@/components/layout/app-header';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { ProductTour } from '@/components/tour/product-tour';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';

/**
 * The authenticated application shell.
 *
 * shadcn's Sidebar primitive handles the collapse behaviour and the mobile
 * drawer, and it reads `dir` from the document — so the sidebar sits on the right
 * in Arabic without any conditional logic here (§1.2).
 */
export function AppLayout({ children }: { children: ReactNode }) {
    const { t } = useTranslation('nav');

    return (
        <SidebarProvider>
            {/*
             * The first focusable element on the page, visible only when focused.
             * Without it, a keyboard user has to tab through the entire sidebar on
             * every navigation to reach the content (§1.3, WCAG 2.4.1).
             */}
            <a
                href="#main-content"
                className="sr-only-focusable absolute start-4 top-4 z-50 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:ring-2 focus:ring-ring"
            >
                {t('skip_to_content')}
            </a>

            <AppSidebar />

            <SidebarInset className="min-w-0">
                <AppHeader />

                <main
                    id="main-content"
                    data-tour="page"
                    tabIndex={-1}
                    className="min-w-0 flex-1 p-4 focus:outline-none md:p-6"
                >
                    {children}
                </main>
            </SidebarInset>

            {/*
             * Mounted in the shell rather than on a page, because a step can
             * send the reader to another route and the tour has to survive the
             * navigation to carry on from where it was.
             */}
            <ProductTour />
        </SidebarProvider>
    );
}
